import React, { useState, useEffect } from 'react';
import { ShoppingBag, X } from 'lucide-react';

// Sample Indian names and cities for realistic notifications
const names = [
  'Priya', 'Ananya', 'Sneha', 'Kavya', 'Ritika', 'Pooja', 'Neha', 'Shreya',
  'Rahul', 'Amit', 'Vikram', 'Arjun', 'Rohan', 'Karan', 'Varun', 'Aditya',
  'Meera', 'Nisha', 'Divya', 'Tanvi', 'Ishita', 'Sanya', 'Riya', 'Simran'
];

const cities = [
  'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Pune', 'Kolkata',
  'Ahmedabad', 'Jaipur', 'Lucknow', 'Chandigarh', 'Gurgaon', 'Noida', 'Indore',
  'Kochi', 'Nagpur', 'Coimbatore', 'Vadodara', 'Surat', 'Bhopal'
];

const timeAgo = [
  '2 minutes ago',
  '5 minutes ago',
  '8 minutes ago',
  '12 minutes ago',
  '15 minutes ago',
  '20 minutes ago',
  '25 minutes ago',
  'just now'
];

export function SocialProofNotification({ products = [] }) {
  const [visible, setVisible] = useState(false);
  const [notification, setNotification] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (products.length === 0 || dismissed) return;

    const showRandomNotification = () => {
      if (products.length === 0) return;

      const randomProduct = products[Math.floor(Math.random() * products.length)];
      const randomName = names[Math.floor(Math.random() * names.length)];
      const randomCity = cities[Math.floor(Math.random() * cities.length)];
      const randomTime = timeAgo[Math.floor(Math.random() * timeAgo.length)];

      setNotification({
        name: randomName,
        city: randomCity,
        time: randomTime,
        product: randomProduct
      });
      setVisible(true);

      // Auto hide after 4 seconds
      setTimeout(() => {
        setVisible(false);
      }, 4000);
    };

    // Show first notification after 5 seconds
    const initialDelay = setTimeout(() => {
      showRandomNotification();
    }, 5000);

    // Then show every 15-30 seconds
    const interval = setInterval(() => {
      if (!dismissed) {
        showRandomNotification();
      }
    }, Math.random() * 15000 + 15000);

    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, [products, dismissed]);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
  };

  if (!visible || !notification) return null;

  return (
    <div 
      className="fixed bottom-24 left-4 z-40 animate-in slide-in-from-left duration-300 max-w-[300px] sm:max-w-[320px]"
      data-testid="social-proof-notification"
    >
      <div className="bg-white rounded-xl shadow-2xl border p-3 flex gap-3 items-start">
        {/* Product Image */}
        <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
          {notification.product?.image_urls?.[0] ? (
            <img 
              src={notification.product.image_urls[0]} 
              alt={notification.product.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-gray-400" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 leading-tight">
            {notification.name} from {notification.city}
          </p>
          <p className="text-xs text-gray-600 mt-0.5">
            purchased{' '}
            <span className="font-medium text-gray-800 line-clamp-1">
              {notification.product?.title}
            </span>
          </p>
          <p className="text-xs text-gray-400 mt-1">{notification.time}</p>
        </div>

        {/* Close Button */}
        <button 
          onClick={handleDismiss}
          className="text-gray-400 hover:text-gray-600 p-1 -mr-1 -mt-1"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default SocialProofNotification;
